// src/features/tasks/components/TaskTrackingHistory.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  Button,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  History as HistoryIcon,
  Person as PersonIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  CheckCircle as CheckCircleIcon,
  Stop as StopIcon,
  Edit as EditIcon,
  Assignment as AssignmentIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import LocalizedDateDistance from '../../../../../../i18n/LocalizedDateDistance';
import { capitalizeFirst } from '../../../../../../utils/formatters/capitalize';
import ProfilePicture from '../../../../../../components/common/ProfilePicture';

// Hooks
import { useTaskTracking } from '../../../../hooks/useTaskTracking';
import { parseApiTimestamp } from '../../../../../../utils/formatters/dateFormatter';

const TaskTrackingHistory = React.memo(function TaskTrackingHistory({
  taskId,
  doUpdate,
  showSummary = true,
  maxItems = 50,
  compact = false,
}) {
  const { t } = useTranslation(['common', 'tasks']);
  const [showDetails, setShowDetails] = useState(false);
  const [limit, setLimit] = useState(20);

  const {
    taskHistory,
    taskActivitySummary,
    loading,
    error,
    clearError,
    fetchTaskTrackingHistory,
    fetchTaskActivitySummary,
  } = useTaskTracking();

  // Tracking type configurations
  const TRACKING_TYPE_CONFIG = {
    creation: {
      icon: AssignmentIcon,
      color: 'success',
      label: t('tasks:tracking_types.creation'),
      description: t('tasks:tracking_types.creation')
    },
    state_change: {
      icon: PlayArrowIcon,
      color: 'info',
      label: t('tasks:tracking_types.state_change'),
      description: t('tasks:tracking_types.state_change')
    },
    assignment_change: {
      icon: PersonIcon,
      color: 'secondary',
      label: t('tasks:tracking_types.assignment_change'),
      description: t('tasks:tracking_types.assignment_change')
    },
    property_update: {
      icon: EditIcon,
      color: 'warning',
      label: t('tasks:tracking_types.property_update'),
      description: t('tasks:tracking_types.property_update')
    },
    completion: {
      icon: CheckCircleIcon,
      color: 'success',
      label: t('tasks:tracking_types.completion'),
      description: t('tasks:tracking_types.completion')
    },
    reopen: {
      icon: RefreshIcon,
      color: 'error',
      label: t('tasks:tracking_types.reopen'),
      description: t('tasks:tracking_types.reopen')
    },
  };

  const loadTrackingData = useCallback(async () => {
    try {
      const params = { limit, offset: 0 };
      await Promise.all([
        fetchTaskTrackingHistory(taskId, params),
        showSummary && fetchTaskActivitySummary(taskId),
      ].filter(Boolean));
    } catch (error) {
      console.error('Failed to load tracking data:', error);
    }
  }, [taskId, limit, fetchTaskTrackingHistory, fetchTaskActivitySummary, showSummary]);

  // Load data when component mounts or taskId changes
  useEffect(() => {
    if (taskId) {
      loadTrackingData();
    }
  }, [taskId, doUpdate, limit, loadTrackingData]);

  const handleLoadMore = useCallback(() => {
    setLimit(prevLimit => Math.min(prevLimit + 20, maxItems));
  }, [maxItems]);

  const handleRefresh = useCallback(() => {
    loadTrackingData();
  }, [loadTrackingData]);

  const handleToggleDetails = useCallback(() => {
    setShowDetails(prev => !prev);
  }, []);

  // Render activity summary card
  const renderActivitySummary = () => {
    if (!showSummary || !taskActivitySummary) return null;

    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="between" mb={2}>
            <Typography variant="h6" display="flex" alignItems="center" gap={1}>
              <HistoryIcon color="primary" />
              {t('tasks:tracking.activity_summary')}
            </Typography>
            <Tooltip title={t('tasks:actions.refresh')}>
              <IconButton size="small" onClick={handleRefresh} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={2}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('tasks:tracking.total_changes')}
              </Typography>
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                {taskActivitySummary.total_changes}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('tasks:tracking.state_changes')}
              </Typography>
              <Typography variant="h4" color="info.main" fontWeight="bold">
                {taskActivitySummary.state_changes}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('tasks:tracking.assignment_changes')}
              </Typography>
              <Typography variant="h4" color="secondary.main" fontWeight="bold">
                {taskActivitySummary.assignment_changes}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('tasks:tracking.property_updates')}
              </Typography>
              <Typography variant="h4" color="warning.main" fontWeight="bold">
                {taskActivitySummary.property_updates}
              </Typography>
            </Box>
          </Box>

          {taskActivitySummary.last_activity && (
            <Box mt={2} pt={2} borderTop={1} borderColor="divider">
              <Typography variant="body2" color="text.secondary">
                {t('tasks:tracking.last_activity', { 
                  type: taskActivitySummary.last_activity_type, 
                  by: taskActivitySummary.last_activity_by 
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                <LocalizedDateDistance date={taskActivitySummary.last_activity} />
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  // Render individual tracking entry
  const renderTrackingEntry = (entry) => {
    console.log(entry)
    const config = TRACKING_TYPE_CONFIG[entry.tracking_type] || TRACKING_TYPE_CONFIG.property_update;
    const IconComponent = config.icon;

    // Check if this is a simple state change
    const isSimpleStateChange = entry.tracking_type === 'state_change' && 
      entry.before_values?.state && 
      entry.after_values?.state;

    return (
      <TimelineItem key={entry.tracking_id} sx={{ minHeight: 'auto' }}>
        <TimelineOppositeContent 
          sx={{ 
            m: 0, 
            p: 1,
            flex: 'none',
            maxWidth: '120px'
          }} 
          align="center" 
          variant="body2" 
          color="text.secondary"
        >
          <Typography variant="caption" display="block">
            {format(parseApiTimestamp(entry.created_at), 'MMM dd')}
          </Typography>
          <Typography variant="caption" display="block">
            {format(parseApiTimestamp(entry.created_at), 'HH:mm')}
          </Typography>
        </TimelineOppositeContent>

        <TimelineSeparator>
          <TimelineDot color={config.color} sx={{ my: 0 }}>
            <IconComponent fontSize="small" />
          </TimelineDot>
          <TimelineConnector sx={{ minHeight: '20px' }} />
        </TimelineSeparator>

        <TimelineContent sx={{ py: 1, px: 2, minWidth: '300px' }}>
          <Card variant="outlined" sx={{ mb: 1 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              {isSimpleStateChange ? (
                // Simple state change - show arrow format
                <>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle1" color={`${config.color}.main`}>
                      {t('tasks:tracking.state_changed')}
                    </Typography>
                    <Chip 
                      label={t('tasks:tracking_types.state_change')}
                      size="small" 
                      color={config.color}
                      variant="outlined" 
                    />
                  </Box>

                  <Typography variant="body2" color='text.secondary' sx={{ mb: 2, fontFamily: 'monospace' }}>
                    {entry.before_values.state} → {entry.after_values.state}
                  </Typography>

                  <Box display="flex" alignItems="center" gap={1}>
                    <ProfilePicture
                      profilePictureFileId={entry.staff_member_profile_picture_file_id}
                      name={entry.staff_member_name}
                      size={24}
                      imageSize="32x32"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {t('tasks:tracking.by_user', { user: entry.staff_member_name || t('common:unknown') })}
                    </Typography>
                  </Box>
                </>
              ) : (
                // Regular detailed format for other changes
                <>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle1" color={`${config.color}.main`}>
                      {config.label}
                    </Typography>
                    <Chip 
                      label={capitalizeFirst(entry.tracking_type.replace('_', ' '))} 
                      size="small" 
                      color={config.color}
                      variant="outlined" 
                    />
                  </Box>

                  <Typography variant="body2" color='text.secondary' sx={{ mb: 2 }}>
                    {capitalizeFirst(entry.description)}
                  </Typography>

                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <ProfilePicture
                      profilePictureFileId={entry.staff_member_profile_picture_file_id}
                      name={entry.staff_member_name}
                      size={24}
                      imageSize="32x32"
                    />
                    <Typography variant="body2" color="text.secondary">
                      {t('tasks:tracking.by_user', { user: entry.staff_member_name || t('common:unknown') })}
                    </Typography>
                  </Box>

                  {/* Show before/after values if details are enabled */}
                  {showDetails && (entry.before_values || entry.after_values) && (
                    <Box sx={{ mt: 2, p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                      {entry.before_values && Object.keys(entry.before_values).length > 0 && (
                        <Box mb={1}>
                          <Typography variant="caption" fontWeight="bold" color="error.main">
                            {t('common:before')}:
                          </Typography>
                          <Typography variant="caption" component="pre" sx={{ ml: 1, fontFamily: 'monospace' }}>
                            {JSON.stringify(entry.before_values, null, 2)}
                          </Typography>
                        </Box>
                      )}
                      
                      {entry.after_values && Object.keys(entry.after_values).length > 0 && (
                        <Box>
                          <Typography variant="caption" fontWeight="bold" color="success.main">
                            {t('common:after')}:
                          </Typography>
                          <Typography variant="caption" component="pre" sx={{ ml: 1, fontFamily: 'monospace' }}>
                            {JSON.stringify(entry.after_values, null, 2)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TimelineContent>
      </TimelineItem>
    );
  };

  if (loading && taskHistory.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" py={4}>
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          {t('tasks:messages.loading_history')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Activity Summary */}
      {/* {renderActivitySummary()} */}

      {/* Controls */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <HistoryIcon color="primary" />
          {t('tasks:tracking.title')} ({t('tasks:tracking.entries_count', { count: taskHistory.length })})
        </Typography>

        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={showDetails}
                onChange={handleToggleDetails}
                size="small"
              />
            }
            label={t('tasks:tracking.show_details')}
          />
          
          <Tooltip title={t('tasks:actions.refresh')}>
            <IconButton size="small" onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Timeline */}
      {taskHistory.length === 0 ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={8}
        >
          <HistoryIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('tasks:messages.no_tracking_history')}
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            {t('tasks:messages.activity_will_appear')}
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ overflowX: 'auto', pb: 2 }}>
            <Timeline position="top" sx={{ minWidth: 'max-content' }}>
              {taskHistory.map(renderTrackingEntry)}
            </Timeline>
          </Box>

          {/* Load More Button */}
          {taskHistory.length >= limit && limit < maxItems && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Button
                variant="outlined"
                onClick={handleLoadMore}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <ExpandMoreIcon />}
              >
                {loading ? t('common:messages.loading') : `${t('tasks:actions.load_more')} (${limit}/${maxItems})`}
              </Button>
            </Box>
          )}

          {/* End of history indicator */}
          {taskHistory.length < limit || limit >= maxItems ? (
            <Box display="flex" justifyContent="center" mt={3}>
              <Typography variant="caption" color="text.secondary">
                {t('tasks:messages.end_of_history')}
              </Typography>
            </Box>
          ) : null}
        </>
      )}
    </Box>
  );
});

export default TaskTrackingHistory;